/**
 * P4 — per-brand client-portal settings (`/client-reports/settings`).
 *
 * Small per-brand card: changelog on/off (+ lookback days), deep-dive on/off.
 * PUT shops/{slug}/client-portal-settings. Gated on `portal.settings.manage`.
 * Dirty-tracked against the loaded settings.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import ToggleSwitch from '../../../ui/buttons/ToggleSwitch';
import { useCan } from '../../../lib/rbac';
import { setMessage } from '../../../store/reducers/appSlice';
import { HOME_ROUTE } from '../../../lib/constants/route';
import type { RootState } from '../../../store/store';
import ClientReportsNav from '../ClientReportsNav';
import PortalDomainsSection from './PortalDomainsSection';
import {
  getClientPortalSettings,
  updateClientPortalSettings,
  type ClientPortalSettings,
} from '../../../lib/api/client-reports/report-branding-api';
import '../client-reports.scss';

interface Draft {
  changelog_enabled: boolean;
  changelog_lookback_days: number;
  deep_dive_enabled: boolean;
}

const DEFAULT_LOOKBACK = 30;

function draftFrom(s: ClientPortalSettings | null): Draft {
  return {
    changelog_enabled: s?.changelog_enabled ?? false,
    changelog_lookback_days: s?.changelog_lookback_days ?? DEFAULT_LOOKBACK,
    deep_dive_enabled: s?.deep_dive_enabled ?? false,
  };
}

const PortalBrandSettings = () => {
  const dispatch = useDispatch();
  const canManage = useCan('portal.settings.manage');
  // Portal domains are agency-wide and gated separately: a user with settings
  // access but not `portal.domains.manage` sees the rest of settings but not
  // the domains section.
  const canDomains = useCan('portal.domains.manage');

  // Operate on the GLOBAL active brand (switched from the bottom-left sidebar
  // button) instead of an in-page brand selector.
  const activeShopSlug = useSelector(
    (s: RootState) => s.user?.profile?.active_shop_slug || ''
  );
  const allShops = useSelector(
    (s: RootState) => s.user?.profile?.all_shops || []
  );
  const activeShopName = useMemo(
    () =>
      allShops.find((sh: any) => sh?.slug === activeShopSlug)?.name ||
      activeShopSlug,
    [allShops, activeShopSlug]
  );

  const [original, setOriginal] = useState<ClientPortalSettings | null>(null);
  const [draft, setDraft] = useState<Draft>(draftFrom(null));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    async (slug: string) => {
      if (!slug) return;
      setLoading(true);
      try {
        const s = await getClientPortalSettings(slug);
        setOriginal(s);
        setDraft(draftFrom(s));
      } catch (err: any) {
        const msg =
          err?.status === 403
            ? 'You do not have access to that brand.'
            : err?.message || 'Failed to load portal settings.';
        dispatch(setMessage({ message: msg, messageType: 'error' }));
        setOriginal(null);
        setDraft(draftFrom(null));
      } finally {
        setLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (activeShopSlug) load(activeShopSlug);
  }, [activeShopSlug, load]);

  const isDirty = useMemo(() => {
    const base = draftFrom(original);
    return (
      base.changelog_enabled !== draft.changelog_enabled ||
      base.changelog_lookback_days !== draft.changelog_lookback_days ||
      base.deep_dive_enabled !== draft.deep_dive_enabled
    );
  }, [original, draft]);

  const handleSave = async () => {
    if (!activeShopSlug || !isDirty || saving) return;
    setSaving(true);
    try {
      const updated = await updateClientPortalSettings(activeShopSlug, {
        changelog_enabled: draft.changelog_enabled,
        changelog_lookback_days: draft.changelog_lookback_days,
        deep_dive_enabled: draft.deep_dive_enabled,
      });
      setOriginal(updated);
      setDraft(draftFrom(updated));
      dispatch(
        setMessage({ message: 'Portal settings saved.', messageType: 'success' })
      );
    } catch (err: any) {
      dispatch(
        setMessage({
          message: err?.message || 'Failed to save portal settings.',
          messageType: 'error',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return (
      <SubPageInnerContentHolder title='Portal settings' backBtnLink={HOME_ROUTE}>
        <div className='client-reports__empty'>
          You don't have permission to manage portal settings. Ask an owner or
          manager for the "manage portal settings" capability.
        </div>
      </SubPageInnerContentHolder>
    );
  }

  return (
    <SubPageInnerContentHolder title='Portal settings' backBtnLink={HOME_ROUTE}>
      <ClientReportsNav />

      <div className='portal-settings'>
        <div className='portal-settings__header'>
          <div className='portal-settings__field'>
            <label className='portal-settings__label'>Active brand</label>
            <div className='client-reports__active-brand'>
              <span className='client-reports__active-brand-name'>
                {activeShopName || '—'}
              </span>
              <span className='client-reports__active-brand-hint'>
                Switch brand from the button in the bottom-left.
              </span>
            </div>
          </div>
          <div className='portal-settings__header-save'>
            <MainSquareButton
              type='button'
              value={saving ? 'Saving…' : 'Save settings'}
              buttonType='success'
              normal
              loading={saving}
              disabled={!isDirty || saving || loading}
              onClick={handleSave}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ marginTop: '3rem', textAlign: 'center' }}>
            <LoadingSpinner customLoadingMsg='Loading settings…' />
          </div>
        ) : !activeShopSlug ? (
          <div className='client-reports__empty'>
            No active brand. Pick one from the button in the bottom-left.
          </div>
        ) : (
          <section className='portal-settings__card'>
            <div className='portal-settings__row'>
              <div className='portal-settings__row-text'>
                <span className='portal-settings__row-title'>Changelog</span>
                <span className='portal-settings__row-hint'>
                  Show a client-friendly "what we did" timeline in the portal,
                  built from approved actions.
                </span>
              </div>
              <ToggleSwitch
                id='ps-changelog'
                isOn={draft.changelog_enabled}
                onClick={(checked: boolean) =>
                  setDraft((d) => ({ ...d, changelog_enabled: checked }))
                }
              />
            </div>

            {draft.changelog_enabled && (
              <div className='portal-settings__row portal-settings__row--sub'>
                <div className='portal-settings__row-text'>
                  <span className='portal-settings__row-title'>
                    Changelog lookback
                  </span>
                  <span className='portal-settings__row-hint'>
                    How many days of history the changelog covers.
                  </span>
                </div>
                <div className='portal-settings__lookback'>
                  <input
                    className='portal-settings__number'
                    type='number'
                    min={1}
                    max={365}
                    value={draft.changelog_lookback_days}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        changelog_lookback_days: Math.max(
                          1,
                          Math.min(365, Number(e.target.value) || DEFAULT_LOOKBACK)
                        ),
                      }))
                    }
                  />
                  <span className='portal-settings__unit'>days</span>
                </div>
              </div>
            )}

            <div className='portal-settings__row'>
              <div className='portal-settings__row-text'>
                <span className='portal-settings__row-title'>Deep dives</span>
                <span className='portal-settings__row-hint'>
                  Let clients expand the deep-dive sections inside reports.
                </span>
              </div>
              <ToggleSwitch
                id='ps-deepdive'
                isOn={draft.deep_dive_enabled}
                onClick={(checked: boolean) =>
                  setDraft((d) => ({ ...d, deep_dive_enabled: checked }))
                }
              />
            </div>
          </section>
        )}

        {/* ---- Portal domains (agency-wide; folded in from the old Domains tab) ---- */}
        {canDomains && <PortalDomainsSection />}
      </div>
    </SubPageInnerContentHolder>
  );
};

export default PortalBrandSettings;
